import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
  Linking,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackParamList, RootStackNavigationProp } from '../../../../types/navigation';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('CasketUrnInputCasket');

import {
  CasketDetails,
  CasketUrnDetails,
  casketUrnInfoService
} from '../../../../services/casketUrnInfo';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type CasketUrnInputCasketScreenRouteProp = RouteProp<RootStackParamList, 'CasketUrnInputCasket'>;

type CasketType = 'metal' | 'wood' | 'fiberglass' | 'bio' | 'alternative' | null;

export const CasketUrnInputCasketScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<CasketUrnInputCasketScreenRouteProp>();

  const { casketUrnData, isEditing } = route.params || {};

  const [selectedCasket, setSelectedCasket] = useState<CasketType>(
    casketUrnData?.specificType as CasketType || null
  );
  const [specialInstructions, setSpecialInstructions] = useState(
    casketUrnData?.specialInstructions || ''
  );

  // Initialize details with existing data if editing
  const getInitialDetails = () => {
    if (casketUrnData?.details && casketUrnData.containerType === 'casket') {
      return casketUrnData.details as CasketDetails;
    }
    return {};
  };

  const initialDetails = getInitialDetails();

  // Metal Casket Details
  const [metalDetails, setMetalDetails] = useState<CasketDetails>({
    material: initialDetails?.material || '',
    color: initialDetails?.color || '',
    style: initialDetails?.style || '',
    personalization: initialDetails?.personalization || '',
    customMade: initialDetails?.customMade || false,
    specifications: initialDetails?.specifications || '',
  });

  // Wood Casket Details
  const [woodDetails, setWoodDetails] = useState<CasketDetails>({
    material: initialDetails?.material || '',
    color: initialDetails?.color || '',
    style: initialDetails?.style || '',
    personalization: initialDetails?.personalization || '',
    customMade: initialDetails?.customMade || false,
    specifications: initialDetails?.specifications || '',
  });

  // Fiberglass Casket Details
  const [fiberglassDetails, setFiberglassDetails] = useState<CasketDetails>({
    material: initialDetails?.material || '',
    color: initialDetails?.color || '',
    style: initialDetails?.style || '',
    personalization: initialDetails?.personalization || '',
    customMade: initialDetails?.customMade || false,
    specifications: initialDetails?.specifications || '',
  });

  // Biodegradable Casket Details
  const [bioDetails, setBioDetails] = useState<CasketDetails>({
    material: initialDetails?.material || '',
    color: initialDetails?.color || '',
    style: initialDetails?.style || '',
    personalization: initialDetails?.personalization || '',
    customMade: initialDetails?.customMade || false,
    specifications: initialDetails?.specifications || '',
  });

  // Alternative Casket Details
  const [alternativeDetails, setAlternativeDetails] = useState<CasketDetails>({
    material: initialDetails?.material || '',
    color: initialDetails?.color || '',
    style: initialDetails?.style || '',
    personalization: initialDetails?.personalization || '',
    customMade: initialDetails?.customMade || false,
    specifications: initialDetails?.specifications || '',
  });

  const handleSave = async () => {
    try {
      // Determine which casket details to save based on selected type
      let casketDetails: CasketUrnDetails = null;

      switch (selectedCasket) {
        case 'metal':
          casketDetails = metalDetails;
          break;
        case 'wood':
          casketDetails = woodDetails;
          break;
        case 'fiberglass':
          casketDetails = fiberglassDetails;
          break;
        case 'bio':
          casketDetails = bioDetails;
          break;
        case 'alternative':
          casketDetails = alternativeDetails;
          break;
      }

      // Prepare the data to save
      const casketUrnUpdateData = {
        containerType: 'casket',
        specificType: selectedCasket || '',
        specialInstructions: specialInstructions,
        details: casketDetails,
        notes: specialInstructions, // Map specialInstructions to notes field
      };

      let savedCasketUrn;

      if (isEditing && casketUrnData?.id) {
        // Update existing record
        savedCasketUrn = await casketUrnInfoService.updateCasketUrnInfo(
          casketUrnData.id,
          casketUrnUpdateData
        );
        logger.debug('Casket information updated successfully with ID:', savedCasketUrn.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        (navigation as any).navigate('CasketUrnLockUnlock', { casketUrnId: savedCasketUrn.id });
      } else {
        // Create new record
        savedCasketUrn = await casketUrnInfoService.createCasketUrnInfo(casketUrnUpdateData);
        logger.debug('Casket information saved successfully with ID:', savedCasketUrn.id);

        // Navigate to lock/unlock screen with the ID
        (navigation as any).navigate('CasketUrnLockUnlock', { casketUrnId: savedCasketUrn.id });
      }
    } catch (error) {
      logger.error('Error saving casket information:', error);
    }
  };

  const handleLinkPress = () => {
    Linking.openURL('https://titancasket.com/collections');
  };

  const renderDetailsInput = () => {
    switch (selectedCasket) {
      case 'metal':
        return (
          <View>
            <Text style={styles.detailsTitle}>Metal Casket Details:</Text>

            {/* Material Input */}
            <Text style={styles.inputLabel}>Type of metal (brass, bronze, steel, etc.):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify the type of metal..."
              value={metalDetails.material}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, material: value })
              }
              multiline
            />

            {/* Color Input */}
            <Text style={styles.inputLabel}>Color preference:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your preferred color or finish..."
              value={metalDetails.color}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, color: value })
              }
              multiline
            />

            {/* Style Input */}
            <Text style={styles.inputLabel}>Style (Modern or Traditional):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify your preferred style..."
              value={metalDetails.style}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, style: value })
              }
              multiline
            />

            {/* Personalization Input */}
            <Text style={styles.inputLabel}>Personalization options:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any engravings, emblems, or custom features..."
              value={metalDetails.personalization}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, personalization: value })
              }
              multiline
            />

            {/* Custom Made Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Would you like a custom-made casket?</Text>
              <Switch
                value={metalDetails.customMade}
                onValueChange={(value) =>
                  setMetalDetails({ ...metalDetails, customMade: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={metalDetails.customMade ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Additional Specifications */}
            <Text style={styles.inputLabel}>Additional specifications:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Any other specific requirements or preferences..."
              value={metalDetails.specifications}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, specifications: value })
              }
              multiline
            />

            <Text style={[styles.detailsTitle, { marginTop: 16 }]}>Buy your casket ahead of time:</Text>
            <TouchableOpacity onPress={handleLinkPress}>
              <Text style={styles.linkText}>Click HERE for our recommended selection.</Text>
            </TouchableOpacity>
          </View>
        );
      case 'wood':
        return (
          <View>
            <Text style={styles.detailsTitle}>Wood Casket Details:</Text>

            {/* Material Input */}
            <Text style={styles.inputLabel}>Type of wood (oak, maple, cherry, etc.):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify the type of wood..."
              value={woodDetails.material}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, material: value })
              }
              multiline
            />

            {/* Color Input */}
            <Text style={styles.inputLabel}>Color or finish preference:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your preferred stain, finish, or natural look..."
              value={woodDetails.color}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, color: value })
              }
              multiline
            />

            {/* Style Input */}
            <Text style={styles.inputLabel}>Style (Modern or Traditional):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify your preferred style..."
              value={woodDetails.style}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, style: value })
              }
              multiline
            />

            {/* Personalization Input */}
            <Text style={styles.inputLabel}>Personalization options:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any carvings, engravings, or custom features..."
              value={woodDetails.personalization}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, personalization: value })
              }
              multiline
            />

            {/* Custom Made Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Would you like a custom-made casket?</Text>
              <Switch
                value={woodDetails.customMade}
                onValueChange={(value) =>
                  setWoodDetails({ ...woodDetails, customMade: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={woodDetails.customMade ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Additional Specifications */}
            <Text style={styles.inputLabel}>Additional specifications:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Any other specific requirements or preferences..."
              value={woodDetails.specifications}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, specifications: value })
              }
              multiline
            />

            <Text style={[styles.detailsTitle, { marginTop: 16 }]}>Buy your casket ahead of time:</Text>
            <TouchableOpacity onPress={handleLinkPress}>
              <Text style={styles.linkText}>Click HERE for our recommended selection.</Text>
            </TouchableOpacity>
          </View>
        );
      case 'fiberglass':
        return (
          <View>
            <Text style={styles.detailsTitle}>Fiberglass Casket Details:</Text>

            {/* Material Input */}
            <Text style={styles.inputLabel}>Type of fiberglass construction:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify the fiberglass type or construction method..."
              value={fiberglassDetails.material}
              onChangeText={(value) =>
                setFiberglassDetails({ ...fiberglassDetails, material: value })
              }
              multiline
            />

            {/* Color Input */}
            <Text style={styles.inputLabel}>Color preference:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your preferred color or finish..."
              value={fiberglassDetails.color}
              onChangeText={(value) =>
                setFiberglassDetails({ ...fiberglassDetails, color: value })
              }
              multiline
            />

            {/* Style Input */}
            <Text style={styles.inputLabel}>Style (Modern or Traditional):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify your preferred style..."
              value={fiberglassDetails.style}
              onChangeText={(value) =>
                setFiberglassDetails({ ...fiberglassDetails, style: value })
              }
              multiline
            />

            {/* Personalization Input */}
            <Text style={styles.inputLabel}>Personalization options:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any custom designs, patterns, or features..."
              value={fiberglassDetails.personalization}
              onChangeText={(value) =>
                setFiberglassDetails({ ...fiberglassDetails, personalization: value })
              }
              multiline
            />

            {/* Custom Made Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Would you like a custom-made casket?</Text>
              <Switch
                value={fiberglassDetails.customMade}
                onValueChange={(value) =>
                  setFiberglassDetails({ ...fiberglassDetails, customMade: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={fiberglassDetails.customMade ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Additional Specifications */}
            <Text style={styles.inputLabel}>Additional specifications:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Any other specific requirements or preferences..."
              value={fiberglassDetails.specifications}
              onChangeText={(value) =>
                setFiberglassDetails({ ...fiberglassDetails, specifications: value })
              }
              multiline
            />

            <Text style={[styles.detailsTitle, { marginTop: 16 }]}>Buy your casket ahead of time:</Text>
            <TouchableOpacity onPress={handleLinkPress}>
              <Text style={styles.linkText}>Click HERE for our recommended selection.</Text>
            </TouchableOpacity>
          </View>
        );
      case 'bio':
        return (
          <View>
            <Text style={styles.detailsTitle}>Biodegradable Casket Details:</Text>

            {/* Material Input */}
            <Text style={styles.inputLabel}>Biodegradable material preference:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Willow, bamboo, seagrass, cardboard, pine, wool, etc..."
              value={bioDetails.material}
              onChangeText={(value) =>
                setBioDetails({ ...bioDetails, material: value })
              }
              multiline
            />

            {/* Style Input */}
            <Text style={styles.inputLabel}>Construction style:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Woven, traditional build, minimalist, etc..."
              value={bioDetails.style}
              onChangeText={(value) =>
                setBioDetails({ ...bioDetails, style: value })
              }
              multiline
            />

            {/* Personalization Input */}
            <Text style={styles.inputLabel}>Natural personalization options:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Natural dyes, woven patterns, eco-friendly decorations..."
              value={bioDetails.personalization}
              onChangeText={(value) =>
                setBioDetails({ ...bioDetails, personalization: value })
              }
              multiline
            />

            {/* Custom Made Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Would you like a custom-made biodegradable casket?</Text>
              <Switch
                value={bioDetails.customMade}
                onValueChange={(value) =>
                  setBioDetails({ ...bioDetails, customMade: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={bioDetails.customMade ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Additional Specifications */}
            <Text style={styles.inputLabel}>Environmental considerations:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Decomposition timeline, burial ground requirements, etc..."
              value={bioDetails.specifications}
              onChangeText={(value) =>
                setBioDetails({ ...bioDetails, specifications: value })
              }
              multiline
            />

            <Text style={[styles.detailsTitle, { marginTop: 16 }]}>Buy your casket ahead of time:</Text>
            <TouchableOpacity onPress={handleLinkPress}>
              <Text style={styles.linkText}>Click HERE for our recommended selection.</Text>
            </TouchableOpacity>
          </View>
        );
      case 'alternative':
        return (
          <View>
            <Text style={styles.detailsTitle}>Alternative Casket Details:</Text>

            {/* Material Input */}
            <Text style={styles.inputLabel}>Alternative material or construction:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Cardboard with artwork, acrylic, recycled materials, stone, etc..."
              value={alternativeDetails.material}
              onChangeText={(value) =>
                setAlternativeDetails({ ...alternativeDetails, material: value })
              }
              multiline
            />

            {/* Style Input */}
            <Text style={styles.inputLabel}>Design concept:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Artistic, transparent, themed, sculptural, etc..."
              value={alternativeDetails.style}
              onChangeText={(value) =>
                setAlternativeDetails({ ...alternativeDetails, style: value })
              }
              multiline
            />

            {/* Personalization Input */}
            <Text style={styles.inputLabel}>Custom artwork or personalization:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Photos, messages, artwork, themed designs, etc..."
              value={alternativeDetails.personalization}
              onChangeText={(value) =>
                setAlternativeDetails({ ...alternativeDetails, personalization: value })
              }
              multiline
            />

            {/* Custom Made Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Would you like a fully custom design?</Text>
              <Switch
                value={alternativeDetails.customMade}
                onValueChange={(value) =>
                  setAlternativeDetails({ ...alternativeDetails, customMade: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={alternativeDetails.customMade ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Additional Specifications */}
            <Text style={styles.inputLabel}>Special requirements:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Weight considerations, display requirements, transportation needs..."
              value={alternativeDetails.specifications}
              onChangeText={(value) =>
                setAlternativeDetails({ ...alternativeDetails, specifications: value })
              }
              multiline
            />

            <Text style={[styles.detailsTitle, { marginTop: 16 }]}>Buy your casket ahead of time:</Text>
            <TouchableOpacity onPress={handleLinkPress}>
              <Text style={styles.linkText}>Click HERE for our recommended selection.</Text>
            </TouchableOpacity>
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          How would you like to be laid to rest after you pass?
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedCasket('metal')}
            >
              <View style={styles.radioButton}>
                {selectedCasket === 'metal' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Metal Casket</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedCasket('wood')}
            >
              <View style={styles.radioButton}>
                {selectedCasket === 'wood' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Wood Casket</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedCasket('fiberglass')}
            >
              <View style={styles.radioButton}>
                {selectedCasket === 'fiberglass' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Fiberglass Casket</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedCasket('bio')}
            >
              <View style={styles.radioButton}>
                {selectedCasket === 'bio' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Biodegradable Casket</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedCasket('alternative')}
            >
              <View style={styles.radioButton}>
                {selectedCasket === 'alternative' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Alternative Casket Options</Text>
            </TouchableOpacity>
          </View>

          {renderDetailsInput()}

          <Text style={styles.label}>Special instructions:</Text>
          <TextInput
            style={styles.specialInstructionsInput}
            placeholder="Enter any special instructions..."
            value={specialInstructions}
            onChangeText={setSpecialInstructions}
            multiline
          />

          <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
            <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(100),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
        position: 'absolute',
        width: '85%',
        height: '85%',
        opacity: 0.1,
      },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  radioGroup: {
    marginBottom: ms(20),
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(20),
  },
  radioButton: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#1C2C4F',
    marginRight: ms(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    width: s(12),
    height: s(12),
    borderRadius: ms(6),
    backgroundColor: '#1C2C4F',
  },
  radioText: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
  },
  structuredInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(15),
    minHeight: s(80),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(15),
    paddingVertical: ms(5),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
    marginRight: ms(15),
  },
  inputLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
    marginTop: ms(5),
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  specialInstructionsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(150),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  detailsTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  bottomPadding: {
    height: s(120),
  },
  linkText: {
    color: '#29BFC0',
    textDecorationLine: 'underline',
    marginLeft: ms(16),
    marginBottom: ms(20),
  },
});

export default CasketUrnInputCasketScreen;
